import React, { useEffect, useState } from 'react';
import { apiUrl } from '../services/api';

const SESSION_KEY = 'meihua_admin_session_v1';
const DEV_ADMIN_EMAIL = 'admin@meihua.local';
const DEV_ADMIN_PASSWORD = 'MeiHuaAdmin2026!';
const defaultAdminEmail = import.meta.env.DEV ? DEV_ADMIN_EMAIL : '';
const defaultAdminPassword = import.meta.env.DEV ? DEV_ADMIN_PASSWORD : '';

type AdminSession = {
  sessionToken: string;
  expiresAt: string;
  admin: { email: string };
};

type AdminSettings = {
  provider: 'google' | 'openai' | 'openrouter' | 'groq';
  model: string;
  apiKeyConfigured: boolean;
  apiKey?: string;
  temperature: number;
  maxOutputTokens: number;
  rateLimitPerMinute: number;
  maintenanceMode: boolean;
  signupsEnabled: boolean;
  freeCreditsOnSignup: number;
  weeklyCreditAmount: number;
  supportEmail: string;
  announcement: string;
  privacyUrl: string;
  termsUrl: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

type ModelOption = {
  id: string;
  name: string;
  contextLength?: number | null;
  description?: string;
  provider?: string;
};

type AdminUser = {
  uid: string;
  email?: string;
  displayName?: string;
  createdAt?: string | null;
  lastSignInAt?: string | null;
  firestore?: Record<string, unknown> | null;
  chatCount?: number;
  creditBalance?: number | null;
};

type AdminOverview = {
  authUserCount: number;
  firestoreUserCount: number;
  users: AdminUser[];
  settings: AdminSettings;
};

type UserDetail = AdminUser & {
  chatHistory: any[];
  credits?: any;
  subscription?: any;
  transactions?: any[];
};

const defaultSettings: AdminSettings = {
  provider: 'google',
  model: 'gemma-3-27b-it',
  apiKeyConfigured: false,
  apiKey: '',
  temperature: 0.7,
  maxOutputTokens: 2048,
  rateLimitPerMinute: 10,
  maintenanceMode: false,
  signupsEnabled: true,
  freeCreditsOnSignup: 10,
  weeklyCreditAmount: 50,
  supportEmail: '',
  announcement: '',
  privacyUrl: '/privacy',
  termsUrl: '/terms',
};

const providerOptions = [
  { id: 'google', label: 'Google', hint: 'Gemini and Gemma models via Gemini API' },
  { id: 'openai', label: 'OpenAI', hint: 'GPT models via Responses API' },
  { id: 'openrouter', label: 'OpenRouter', hint: 'Multi-provider routing and marketplace models' },
  { id: 'groq', label: 'Groq', hint: 'Fast OpenAI-compatible models via GroqCloud' },
] as const;

const defaultModelByProvider: Record<AdminSettings['provider'], string> = {
  google: 'gemma-3-27b-it',
  openai: 'gpt-5.4-mini',
  openrouter: 'google/gemini-2.5-flash',
  groq: 'llama-3.3-70b-versatile',
};

const readStoredSession = (): AdminSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as AdminSession;
    if (!session.sessionToken || new Date(session.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN');
};

const jsonPreview = (value: unknown) => JSON.stringify(value ?? {}, null, 2);

const LogoMark = () => (
  <div className="control-logo" aria-hidden="true">
    <span>梅</span>
    <i />
  </div>
);

export const AdminApp: React.FC = () => {
  const [session, setSession] = useState<AdminSession | null>(() => readStoredSession());
  const [email, setEmail] = useState(defaultAdminEmail);
  const [password, setPassword] = useState(defaultAdminPassword);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [testingModel, setTestingModel] = useState(false);
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);
  const [modelTest, setModelTest] = useState<{ ok: boolean; message: string; responseTime?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'users' | 'settings' | 'diagnostics'>('users');

  const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
    if (!session?.sessionToken) throw new Error('Admin session expired. Please sign in again.');
    const response = await fetch(apiUrl(path), {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.sessionToken}`,
        ...(init?.headers || {}),
      },
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    if (!response.ok) throw new Error(data.message || data.error || `Request failed: ${response.status}`);
    return data.data ?? data;
  };

  const loadOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await request<AdminOverview>('/admin/overview');
      setOverview(data);
      setSettings({ ...defaultSettings, ...data.settings, apiKey: '' });
      if (!selectedUserId && data.users.length > 0) setSelectedUserId(data.users[0].uid);
    } catch (err: any) {
      setError(err?.message || 'Unable to load admin data.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetail = async (uid: string) => {
    try {
      setError(null);
      const data = await request<UserDetail>(`/admin/users/${encodeURIComponent(uid)}`);
      setUserDetail(data);
    } catch (err: any) {
      setError(err?.message || 'Unable to load user details.');
    }
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(apiUrl('/admin/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'Invalid admin login.');
      const nextSession = data.data as AdminSession;
      localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
      setSession(nextSession);
      setNotice('Signed in to MeiHua Control.');
    } catch (err: any) {
      setError(err?.message || 'Admin login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setOverview(null);
    setUserDetail(null);
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const payload = {
        provider: settings.provider,
        model: settings.model.trim(),
        apiKey: settings.apiKey?.trim() || undefined,
      };
      const data = await request<AdminSettings>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setSettings({ ...defaultSettings, ...data, apiKey: '' });
      setNotice('Settings saved. New AI requests will use the updated configuration.');
      await loadOverview();
    } catch (err: any) {
      setError(err?.message || 'Unable to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const loadProviderModels = async () => {
    try {
      setModelsLoading(true);
      setError(null);
      setModelTest(null);
      const data = await request<{ models: ModelOption[]; count: number }>('/admin/models/list', {
        method: 'POST',
        body: JSON.stringify({
          provider: settings.provider,
          apiKey: settings.apiKey?.trim() || undefined,
        }),
      });
      setAvailableModels(data.models);
      setNotice(`Loaded ${data.count} models for ${settings.provider}.`);
      if (!settings.model && data.models[0]) {
        setSettings(prev => ({ ...prev, model: data.models[0].id }));
      }
    } catch (err: any) {
      setAvailableModels([]);
      setError(err?.message || 'Unable to load models for this provider.');
    } finally {
      setModelsLoading(false);
    }
  };

  const testSelectedModel = async () => {
    try {
      setTestingModel(true);
      setError(null);
      setModelTest(null);
      const data = await request<{ provider: string; model: string; snippet: string; responseTime: number }>('/admin/models/test', {
        method: 'POST',
        body: JSON.stringify({
          provider: settings.provider,
          model: settings.model,
          apiKey: settings.apiKey?.trim() || undefined,
        }),
      });
      setModelTest({
        ok: true,
        message: `Loaded successfully: ${data.snippet || data.model}`,
        responseTime: data.responseTime,
      });
    } catch (err: any) {
      setModelTest({ ok: false, message: err?.message || 'Model test failed.' });
    } finally {
      setTestingModel(false);
    }
  };

  useEffect(() => {
    if (session) loadOverview();
  }, [session]);

  useEffect(() => {
    if (session && selectedUserId) loadUserDetail(selectedUserId);
  }, [session, selectedUserId]);

  if (!session) {
    return (
      <main className="control-login-page">
        <section className="control-login-visual">
          <LogoMark />
          <div>
            <p className="control-eyebrow">Hidden Control Surface</p>
            <h1>MeiHua Control</h1>
            <p>Operational access for model settings, Firebase visibility, and account support.</p>
          </div>
        </section>

        <form className="control-login-card" onSubmit={handleLogin}>
          <div className="control-card-head">
            <h2>Admin Sign In</h2>
            <p>Restricted operator console.</p>
          </div>
          {error && <div className="control-alert">{error}</div>}
          <label>
            <span>Admin ID</span>
            <input value={email} onChange={event => setEmail(event.target.value)} autoComplete="username" />
          </label>
          <label>
            <span>Password</span>
            <input value={password} onChange={event => setPassword(event.target.value)} type="password" autoComplete="current-password" />
          </label>
          <button className="control-primary" disabled={loading}>
            {loading ? 'Checking...' : 'Enter Control'}
          </button>
          {import.meta.env.DEV && (
            <div className="control-test-note">
              Local test login: <strong>{DEV_ADMIN_EMAIL}</strong> / <strong>{DEV_ADMIN_PASSWORD}</strong>
            </div>
          )}
          <div className="control-warning">
            Use production admin secrets and Cloudflare Access before sharing this console.
          </div>
        </form>
      </main>
    );
  }

  return (
    <main className="control-page">
      <aside className="control-sidebar">
        <LogoMark />
        <div>
          <p className="control-eyebrow">Signed in as</p>
          <strong>{session.admin.email}</strong>
        </div>
        <nav>
          <button className={activePanel === 'users' ? 'active' : ''} onClick={() => setActivePanel('users')}>Users</button>
          <button className={activePanel === 'settings' ? 'active' : ''} onClick={() => setActivePanel('settings')}>Settings</button>
          <button className={activePanel === 'diagnostics' ? 'active' : ''} onClick={() => setActivePanel('diagnostics')}>Diagnostics</button>
        </nav>
        <button className="control-ghost" onClick={handleLogout}>Sign out</button>
      </aside>

      <section className="control-main">
        <header className="control-topbar">
          <div>
            <p className="control-eyebrow">MeiHua Control</p>
            <h1>{activePanel === 'users' ? 'Users & Firebase' : activePanel === 'settings' ? 'App Settings' : 'Diagnostics'}</h1>
          </div>
          <button className="control-secondary" onClick={loadOverview} disabled={loading}>Refresh</button>
        </header>

        {error && <div className="control-alert">{error}</div>}
        {notice && <div className="control-alert success">{notice}</div>}

        <section className="control-stats">
          <div><span>Auth Users</span><strong>{overview?.authUserCount ?? '-'}</strong></div>
          <div><span>Firestore Users</span><strong>{overview?.firestoreUserCount ?? '-'}</strong></div>
          <div><span>Model</span><strong>{overview?.settings.model || settings.model}</strong></div>
          <div><span>API Key</span><strong>{overview?.settings.apiKeyConfigured ? 'Configured' : 'Missing'}</strong></div>
        </section>

        {activePanel === 'users' && (
          <section className="control-two-col">
            <div className="control-panel">
              <div className="control-panel-head">
                <h2>User Accounts</h2>
                <span>{overview?.users.length ?? 0} loaded</span>
              </div>
              <div className="control-user-list">
                {overview?.users.map(item => (
                  <button
                    key={item.uid}
                    className={selectedUserId === item.uid ? 'selected' : ''}
                    onClick={() => setSelectedUserId(item.uid)}
                  >
                    <strong>{item.displayName || item.email || 'Unnamed user'}</strong>
                    <span>{item.email || item.uid}</span>
                    <small>ID: {item.uid}</small>
                  </button>
                ))}
              </div>
            </div>

            <div className="control-panel">
              <div className="control-panel-head">
                <h2>User Detail</h2>
                <span>{userDetail ? `${userDetail.chatHistory.length} chats` : 'Select user'}</span>
              </div>
              {userDetail ? (
                <div className="control-detail">
                  <div><span>Account ID</span><strong>{userDetail.uid}</strong></div>
                  <div><span>Email</span><strong>{userDetail.email || 'Not recorded'}</strong></div>
                  <div><span>Created</span><strong>{formatDate(userDetail.createdAt)}</strong></div>
                  <div><span>Last sign in</span><strong>{formatDate(userDetail.lastSignInAt)}</strong></div>
                  <div><span>Credits</span><strong>{userDetail.creditBalance ?? userDetail.credits?.balance ?? 'Not recorded'}</strong></div>
                  <h3>Firebase User Document</h3>
                  <pre>{jsonPreview(userDetail.firestore)}</pre>
                  <h3>Recent Chats</h3>
                  {userDetail.chatHistory.length === 0 && <p className="control-muted">No chat history yet.</p>}
                  {userDetail.chatHistory.map(chat => (
                    <article className="control-chat" key={chat.id}>
                      <strong>{chat.question || 'Untitled question'}</strong>
                      <span>{formatDate(chat.timestamp || chat.createdAt)}</span>
                      <pre>{jsonPreview(chat.response)}</pre>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="control-muted">Choose an account to inspect Firebase data.</p>
              )}
            </div>
          </section>
        )}

        {activePanel === 'settings' && (
          <section className="control-command-center">
            <div className="control-command-hero">
              <div>
                <p className="control-eyebrow">Model Command Center</p>
                <h2>{providerOptions.find(provider => provider.id === settings.provider)?.label || 'Google'} / {settings.model}</h2>
                <p>Discover, test, and promote the primary model used by the web app.</p>
              </div>
              <div className="control-live-pill">
                <span>{settings.apiKeyConfigured ? 'API key saved' : 'API key missing'}</span>
                <strong>{modelTest?.ok ? 'Test passed' : 'Ready to test'}</strong>
              </div>
            </div>

            <div className="control-command-layout">
              <div className="control-panel control-model-panel">
                <div className="control-panel-head">
                  <div>
                    <h2>Primary Model</h2>
                    <p className="control-muted">Choose a provider, discover models with your key, test it, then save it as the live model.</p>
                  </div>
                  <span>{settings.apiKeyConfigured ? 'Key saved' : 'No saved key'}</span>
                </div>

              <div className="control-provider-grid">
                {providerOptions.map(provider => (
                  <button
                    type="button"
                    key={provider.id}
                    className={settings.provider === provider.id ? 'selected' : ''}
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        provider: provider.id,
                        model: defaultModelByProvider[provider.id],
                        apiKey: '',
                      }));
                      setAvailableModels([]);
                      setModelTest(null);
                    }}
                  >
                    <strong>{provider.label}</strong>
                    <span>{provider.hint}</span>
                  </button>
                ))}
              </div>

              <div className="control-model-composer">
                <label>
                  <span>API Key</span>
                  <input type="password" value={settings.apiKey || ''} placeholder={settings.apiKeyConfigured ? 'Leave blank to keep current saved key' : 'Enter API key'} onChange={event => setSettings(prev => ({ ...prev, apiKey: event.target.value }))} />
                </label>
                <button className="control-secondary" type="button" onClick={loadProviderModels} disabled={modelsLoading}>
                  {modelsLoading ? 'Checking...' : 'Check Available Models'}
                </button>
              </div>

              <div className="control-model-picker">
                <label>
                  <span>Selected Model</span>
                  {availableModels.length > 0 ? (
                    <select value={settings.model} onChange={event => setSettings(prev => ({ ...prev, model: event.target.value }))}>
                      {availableModels.map(model => (
                        <option key={model.id} value={model.id}>{model.name} ({model.id})</option>
                      ))}
                    </select>
                  ) : (
                    <input value={settings.model} onChange={event => setSettings(prev => ({ ...prev, model: event.target.value }))} />
                  )}
                </label>
                <button className="control-secondary" type="button" onClick={testSelectedModel} disabled={testingModel || !settings.model.trim()}>
                  {testingModel ? 'Testing...' : 'Test Model'}
                </button>
              </div>

              {availableModels.length > 0 && (
                <div className="control-model-list">
                  {availableModels.slice(0, 12).map(model => (
                    <button type="button" key={model.id} className={settings.model === model.id ? 'selected' : ''} onClick={() => setSettings(prev => ({ ...prev, model: model.id }))}>
                      <strong>{model.name}</strong>
                      <span>{model.id}</span>
                      {model.contextLength ? <small>{model.contextLength.toLocaleString()} context</small> : null}
                    </button>
                  ))}
                </div>
              )}

              {modelTest && (
                <div className={`control-alert ${modelTest.ok ? 'success' : ''}`}>
                  {modelTest.message}{modelTest.responseTime ? ` (${modelTest.responseTime}ms)` : ''}
                </div>
              )}
            </div>

              <aside className="control-panel control-primary-card">
                <span>Primary model candidate</span>
                <h2>{settings.model || 'No model selected'}</h2>
                <dl>
                  <div><dt>Provider</dt><dd>{settings.provider}</dd></div>
                  <div><dt>Key status</dt><dd>{settings.apiKey?.trim() ? 'New key entered' : settings.apiKeyConfigured ? 'Using saved key' : 'Missing key'}</dd></div>
                  <div><dt>Model check</dt><dd>{modelTest?.ok ? 'Loaded successfully' : 'Not confirmed'}</dd></div>
                </dl>
                <button className="control-primary" onClick={saveSettings} disabled={saving || !settings.model.trim()}>
                  {saving ? 'Saving...' : 'Save as Primary Model'}
                </button>
                <p className="control-muted">This updates only the web app model provider, key, and model. Runtime tuning has been removed from this admin screen.</p>
              </aside>
            </div>
          </section>
        )}

        {activePanel === 'diagnostics' && (
          <section className="control-panel">
            <div className="control-panel-head">
              <h2>Production Checklist</h2>
              <span>Security notes</span>
            </div>
            <ul className="control-checklist">
              <li>Deploy the admin app separately from the user app, or protect this hidden route at Cloudflare Access.</li>
              <li>Replace the hardcoded test login with `ADMIN_LOGIN_EMAIL`, `ADMIN_LOGIN_PASSWORD`, and `ADMIN_SESSION_SECRET` secrets.</li>
              <li>Keep Firebase custom claims enabled for long-term admin accounts.</li>
              <li>Rotate the LLM API key after testing if it was shared broadly.</li>
            </ul>
            <pre>{jsonPreview({ apiBase: apiUrl('/'), hiddenPath: '/meihua-control-2026/', sessionExpiresAt: session.expiresAt })}</pre>
          </section>
        )}
      </section>
    </main>
  );
};
